import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ADMIN_NOTIFICATIONS_QUERY_KEY } from '@/hooks/use-admin-notifications'

export const BAN_SUPPORT_QUERY_KEY = ['ban-support'] as const

/** Messages for the signed-in banned user’s thread (RLS restricts to own user_id). */
export function useMyBanSupportThread(enabled: boolean) {
  return useQuery({
    queryKey: [...BAN_SUPPORT_QUERY_KEY, 'mine'],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) return []
      const { data, error } = await supabase
        .from('user_ban_support_messages')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data ?? []
    },
    enabled,
  })
}

export function useSendBanSupportUserMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: string) => {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      if (!user) throw new Error('Not signed in')
      const { error } = await supabase.from('user_ban_support_messages').insert({
        user_id: user.id,
        sender: 'user',
        body: body.trim(),
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...BAN_SUPPORT_QUERY_KEY, 'mine'] })
    },
  })
}

export type BanSupportSidebarUser = { user_id: string; username: string }

/** Distinct banned-support threads (recent activity first) for admins. */
export function useAdminBanSupportSidebar() {
  return useQuery({
    queryKey: [...BAN_SUPPORT_QUERY_KEY, 'admin-sidebar'],
    queryFn: async () => {
      const { data: rows, error: e1 } = await supabase
        .from('user_ban_support_messages')
        .select('user_id, created_at')
        .order('created_at', { ascending: false })
        .limit(500)

      if (e1) throw e1
      if (!rows?.length) return [] as BanSupportSidebarUser[]

      const ordered: string[] = []
      const seen = new Set<string>()
      for (const r of rows) {
        if (!seen.has(r.user_id)) {
          seen.add(r.user_id)
          ordered.push(r.user_id)
        }
      }

      const { data: profs, error: e2 } = await supabase
        .from('profiles')
        .select('user_id, username')
        .in('user_id', ordered)

      if (e2) throw e2
      const nameByUid = Object.fromEntries((profs ?? []).map((p) => [p.user_id, p.username]))
      return ordered.map((uid) => ({
        user_id: uid,
        username: nameByUid[uid] ?? uid.slice(0, 8),
      }))
    },
  })
}

export function useAdminBanSupportThread(threadUserId: string | null) {
  return useQuery({
    queryKey: [...BAN_SUPPORT_QUERY_KEY, 'admin-thread', threadUserId],
    queryFn: async () => {
      if (!threadUserId) return []
      const { data, error } = await supabase
        .from('user_ban_support_messages')
        .select('*')
        .eq('user_id', threadUserId)
        .order('created_at', { ascending: true })

      if (error) throw error
      return data ?? []
    },
    enabled: !!threadUserId,
  })
}

export function useSendBanSupportStaffReply() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      threadUserId,
      body,
      staffProfileId,
    }: {
      threadUserId: string
      body: string
      staffProfileId: string
    }) => {
      const { error } = await supabase.from('user_ban_support_messages').insert({
        user_id: threadUserId,
        sender: 'staff',
        body: body.trim(),
        staff_profile_id: staffProfileId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: BAN_SUPPORT_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ADMIN_NOTIFICATIONS_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}
